













































































































































































































































































































































import Vue from 'vue';
import { mapGetters } from 'vuex';
import {
  LOAD_STATE,
  SAVE_STATE,
  CAMPAIGN_OFFER_TYPE,
  CAMPAIGN_INVOICE_DISCOUNT_TYPE,
  campaignInvoiceDiscountIsPercentage,
  createBaseCampaignOffer,
  DUNNING_DEFAULT_CTA_TAG,
} from '@/store/shared';
import {
  IEmailBlueprint,
  DiscountType,
  CampaignOffer,
  CampaignOfferType,
  ICampaignDiscountOffer,
  ICampaignInvoiceDiscountOffer,
  ICampaignInvoiceDiscountConfig,
  ICoupon,
  ISenderDomain,
  isICampaignDiscountOffer,
  isICampaignInvoiceDiscountOffer,
  CampaignInvoiceDiscountType,
} from '@/store/types';
import { FETCH_ORG_COUPONS, SEND_DUNNING_TEST_EMAIL } from '@/store/action-types';
import EmailContentEditor from '@/components/UI/EmailContentEditor.vue';
import EmailPreview from '@/components/UI/EmailPreview.vue';
import EmailInputDialog from '@/components/UI/EmailInputDialog.vue';
import CouponSelector from '@/components/UI/CouponSelector.vue';
import MiniEditor from '@/components/UI/MiniEditor.vue';

export default Vue.extend({
  name: 'EmailEditor',
  components: {
    EmailPreview,
    EmailInputDialog,
    EmailContentEditor,
    CouponSelector,
    MiniEditor,
  },
  data() {
    return {
      SAVE_STATE,
      LOAD_STATE,
      loadingEmail: true,
      CAMPAIGN_OFFER_TYPE,
      CAMPAIGN_INVOICE_DISCOUNT_TYPE,
      previewEmail: false,
      showEmailInputDialog: false,
      sendingTestEmail: false,
    };
  },
  async created() {
    this.loadingEmail = false;
    this.loadCoupons();
  },
  computed: {
    ...mapGetters(['currentEmailBlueprint', 'currentCampaignBlueprintId']),
    email(): IEmailBlueprint | null {
      return this.$store.getters.currentEmailBlueprint;
    },
    contentWithCTA(): string {
      let content = this.email?.content || '';
      const mergeFieldRegex = /\[\[.*?\]\]/g;
      if (!content.match(mergeFieldRegex)) {
        content = `${content}\n\n${DUNNING_DEFAULT_CTA_TAG}`;
      }
      return content;
    },
    offers(): CampaignOffer[] {
      return this.email?.offers || [];
    },
    hasAnyOffers(): boolean {
      return this.offers.length > 0;
    },
    discountOffer(): ICampaignDiscountOffer | null {
      return this.offers.find(isICampaignDiscountOffer) || null;
    },
    invoiceDiscountOffer(): ICampaignInvoiceDiscountOffer | null {
      return this.offers.find(isICampaignInvoiceDiscountOffer) || null;
    },
    invoiceDiscountIsPercentage(): boolean {
      if (!this.invoiceDiscountOffer) return false;
      return campaignInvoiceDiscountIsPercentage(this.invoiceDiscountOffer);
    },
    invoiceDiscountType(): CampaignInvoiceDiscountType {
      if (!this.invoiceDiscountOffer) return CAMPAIGN_INVOICE_DISCOUNT_TYPE.AMOUNT;
      return this.invoiceDiscountIsPercentage ? CAMPAIGN_INVOICE_DISCOUNT_TYPE.PERCENT : CAMPAIGN_INVOICE_DISCOUNT_TYPE.AMOUNT;
    },
    coupons(): Array<ICoupon> {
      return this.$store.getters.coupons;
    },
    senderDomains(): string[] {
      const domains: ISenderDomain[] = this.$store.state.org.dunning.senderDomains as [ISenderDomain];
      if (domains.length === 0) {
        return [];
      }
      const domainStrings: string[] = domains.filter((domain) => domain.dkimVerified && domain.returnPathVerified).map((domain) => domain.url);
      return domainStrings;
    },
    hasSenderDomains(): boolean {
      return this.senderDomains.length > 0;
    },
    fromDomain(): string {
      if (!this.email) return '';
      const from = this.email.from;
      if (!from) return '';
      const fromDomain = from.split('@');
      if (fromDomain.length < 2) return '';
      return fromDomain[1];
    },
    verifiedFrom(): boolean {
      if (this.fromDomain === '') return true;
      return this.senderDomains.includes(this.fromDomain);
    },
  },
  methods: {
    loadCoupons() {
      this.$store.dispatch(FETCH_ORG_COUPONS);
    },
    toggleOffers(offerType: CampaignOfferType, enabled: boolean) {
      if (!this.email) return;
      if (enabled) {
        this.addOffer(offerType);
        if (this.email.ctaText === '') {
          this.email.ctaText = 'Update Payment & Accept Offers'; // Initialize CTA Text
        }
      } else {
        this.removeOffer(offerType);
        if (this.offers.length === 0) {
          this.email.ctaText = ''; // Reset CTA Text
        }
      }
    },
    addOffer(offerType: CampaignOfferType) {
      const offer = createBaseCampaignOffer(offerType);
      this.$emit('update', this.email, { offers: [...this.offers, offer] });
    },
    removeOffer(offerType: CampaignOfferType) {
      const offers = this.email?.offers?.filter((offer: CampaignOffer) => offer.offerType !== offerType) ?? [];
      this.$emit('update', this.email, { offers });
    },
    // Update selected coupon
    handleCouponSelect(couponId: string) {
      const discountOffer = this.discountOffer;
      if (!discountOffer) return;
      discountOffer.discountConfig.couponId = couponId;
      this.$emit('update', this.email, { offers: [...this.offers] });
    },
    adjustAmount(invoiceDiscountConfig: ICampaignInvoiceDiscountConfig, amount: number) {
      const { type } = invoiceDiscountConfig;
      if (type === CAMPAIGN_INVOICE_DISCOUNT_TYPE.AMOUNT) {
        invoiceDiscountConfig.amount = Math.max(0, amount);
      } else if (type === CAMPAIGN_INVOICE_DISCOUNT_TYPE.PERCENT) {
        invoiceDiscountConfig.amount = Math.max(0, Math.min(100, amount));
      }
      return invoiceDiscountConfig;
    },
    // Update the invoice discount type
    handleInvoiceDiscountTypeChange(type: DiscountType) {
      const offers = this.offers.map((offer: CampaignOffer) => {
        if (isICampaignInvoiceDiscountOffer(offer)) {
          const { amount } = offer.invoiceDiscountConfig;
          offer.invoiceDiscountConfig.type = type;
          offer.invoiceDiscountConfig = this.adjustAmount(offer.invoiceDiscountConfig, amount || 0);
        }
        return offer;
      });
      this.$emit('update', this.email, { offers: [...offers] });
    },
    // Update amount for invoice discount and ensure it is within the allowed range
    handleInvoiceDiscountAmountChange(amount: number) {
      const offers = this.offers.map((offer: CampaignOffer) => {
        if (isICampaignInvoiceDiscountOffer(offer)) {
          offer.invoiceDiscountConfig = this.adjustAmount(offer.invoiceDiscountConfig, amount);
        }
        return offer;
      });
      this.$emit('update', this.email, { offers: [...offers] });
    },
    showPreviewEmail() {
      this.previewEmail = !this.previewEmail;
    },
    async sendTestEmail(emailTo: string): Promise<void> {
      this.sendingTestEmail = true;
      this.showEmailInputDialog = false;
      try {
        if (!emailTo) {
          return;
        }
        const emailBlueprintId = this.currentEmailBlueprint._id;
        const campaignBlueprintId = this.currentCampaignBlueprintId;
        const testEmail = {
          campaignBlueprintId,
          emailBlueprintId,
          emailTo,
        };
        const result = await this.$store.dispatch(SEND_DUNNING_TEST_EMAIL, testEmail);
        if (result.success) {
          this.$notify({
            title: 'Test Email Sent!',
            message: `A test email was sent to ${emailTo}`,
            type: 'success',
            duration: 5000,
          });
        } else {
          throw new Error(result.message || 'Unkonwn Error');
        }
      } catch (e) {
        this.$notify({
          title: 'Error sending Test Email',
          message: e.toString(),
          type: 'error',
          duration: 5000,
        });
      } finally {
        this.sendingTestEmail = false;
      }
    },
  },
});
